import { getListProjectsQueryOptions, useListProjectsSuspense } from '@amarantos/api-client'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Plus, Search } from 'lucide-react'
import { useCallback, useDeferredValue } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { PageContent, PageHeader, PageTitle } from '@/components/page'
import { PaginationBar } from '@/components/pagination-bar'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { TableCardFooter } from '@/components/table-card-footer'
import { TableCardHeader } from '@/components/table-card-header'
import { Button } from '@/components/ui/button'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import { ProjectsTable } from '@/features/projects/components/projects-table'
import { projectsSearchSchema, toListProjectsParams } from '@/features/projects/utils/search'
import { useDebouncedInput } from '@/hooks/use-debounced-input'

export const Route = createFileRoute('/_authenticated/projects/')({
	validateSearch: projectsSearchSchema,
	loaderDeps: ({ search }) => search,
	loader: ({ context: { queryClient }, deps }) => {
		queryClient.ensureQueryData(getListProjectsQueryOptions(toListProjectsParams(deps)))
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_projects' />
	),
	component: ProjectsRoute,
})

function ProjectsRoute() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const navigate = Route.useNavigate()

	const deferredSearch = useDeferredValue(search)
	const isStale = deferredSearch !== search

	const projectsQuery = useListProjectsSuspense(toListProjectsParams(deferredSearch))

	const projects = projectsQuery.data?.data ?? []
	const meta = projectsQuery.data?.meta
	const currentPage = search.page
	const totalPages = meta?.total_pages ?? 1
	const totalCount = meta?.total_count ?? projects.length

	const onDebouncedChange = useCallback(
		(nextValue: string) => {
			navigate({
				replace: true,
				search: (previous) => ({
					...previous,
					search: nextValue,
					page: 1,
				}),
			})
		},
		[navigate],
	)

	const searchInputProps = useDebouncedInput({
		value: search.search,
		onDebouncedChange: onDebouncedChange,
	})

	return (
		<>
			<PageHeader breadcrumbs={[{ label: t('nav.projects') }]}>
				<PageTitle
					subtitle={t('projects.page.description')}
					// The create action rides the title row, as on the users / suppliers pages.
					actions={
						<Button asChild>
							<Link to='/projects/definition'>
								<Plus />
								{t('projects.create.button')}
							</Link>
						</Button>
					}
				>
					{t('nav.projects')}
				</PageTitle>
			</PageHeader>
			<PageContent>
				<div className='bg-card text-card-foreground rounded-xl'>
					{/* The search lives inside the table card, like the users / suppliers list filters —
					    it narrows this table, so it belongs to the block it narrows. */}
					<TableCardHeader>
						<InputGroup className='w-full sm:w-56'>
							<InputGroupAddon>
								<Search />
							</InputGroupAddon>
							<InputGroupInput {...searchInputProps} placeholder={t('projects.search.placeholder')} />
						</InputGroup>
					</TableCardHeader>

					<ProjectsTable projects={projects} isStale={isStale} />

					<TableCardFooter>
						<PaginationBar
							className='w-full'
							currentPage={currentPage}
							totalPages={totalPages}
							onPageChange={(page) => navigate({ search: (previous) => ({ ...previous, page }) })}
							info={
								<Trans
									i18nKey='projects.count'
									values={{ shown: projects.length, total: totalCount }}
									components={{ shown: <span className='font-semibold text-foreground' /> }}
								/>
							}
						/>
					</TableCardFooter>
				</div>
			</PageContent>
		</>
	)
}
